import React from 'react';
import { motion } from 'framer-motion';
import { Clock, User, Calendar, CheckCircle, AlertCircle, ArrowRight, Star } from 'lucide-react';
import { TestimonialCard } from '../components/TestimonialCard';
import { SciencePillar } from '../components/SciencePillar';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function AdvertorialPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-24">
      {/* Navigation / Header */}
      <header className="border-b border-gray-200 py-4 sticky top-0 bg-white/95 backdrop-blur-sm z-50" data-id="element-25">
        <div className="max-w-4xl mx-auto px-4 flex justify-between items-center" data-id="element-26">
          <div className="font-serif font-bold text-xl tracking-tight" data-id="element-27">
            Health & Beauty
          </div>
          <div className="text-xs font-semibold bg-gray-100 px-2 py-1 rounded text-gray-500 uppercase tracking-wider" data-id="element-28">
            Advertisement
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12" data-id="element-29">
        {/* HERO SECTION */}
        <article data-id="element-30">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="mb-8" data-id="element-31">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4 font-serif text-gray-900" data-id="element-32">
              Dermatologists Reveal: Your 'Age Spots' Are 30 Years Old – Here's
              Why You're Only Seeing Them NOW
            </h1>
            <h2 className="text-xl md:text-2xl text-gray-600 leading-relaxed font-light mb-6" data-id="element-33">
              A breakthrough in hormonal research explains why brightening
              creams only fade spots temporarily – and the gentle method that
              actually stops the oxidation process at its source
            </h2>

            <div className="flex items-center text-sm text-gray-500 border-b border-gray-200 pb-6 mb-8" data-id="element-34">
              <div className="flex items-center mr-6" data-id="element-35">
                <User className="w-4 h-4 mr-2" data-id="element-36" />
                <span className="font-medium text-gray-900" data-id="element-37">
                  By Sarah Weber
                </span>
              </div>
              <div className="flex items-center mr-6" data-id="element-38">
                <Calendar className="w-4 h-4 mr-2" data-id="element-39" />
                <span data-id="element-40">Published 2 hours ago</span>
              </div>
              <div className="flex items-center" data-id="element-41">
                <Clock className="w-4 h-4 mr-2" data-id="element-42" />
                <span data-id="element-43">3 min read</span>
              </div>
            </div>
          </motion.div>

          {/* HOOK & PROBLEM */}
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-serif" data-id="element-44">
            <p className="font-bold text-xl mb-6" data-id="element-45">
              The brown spots suddenly appearing on your forehead, cheeks, and
              the backs of your hands are NOT new.
            </p>
            <p data-id="element-46">
              They're not the result of too much sun on your last vacation. And
              they're not simply "part of aging" that you just have to accept.
            </p>
            <p className="bg-yellow-50 p-4 border-l-4 border-yellow-400 italic my-6" data-id="element-47">
              These spots are 30 years old – maybe even older.
            </p>
            <p data-id="element-48">
              If you're over 45 and wondering why these dark spots appeared so
              suddenly... why they're multiplying so fast... and why nothing
              you've tried has worked long-term...
            </p>
            <p data-id="element-49">Then read the next 3 minutes carefully.</p>
            <p data-id="element-50">
              Because you're about to learn why your age spots are actually UV
              damage from your 20s and 30s – and what a leading dermatologist
              developed after she herself spent years searching for a real
              solution.
            </p>

            <h3 className="text-2xl font-bold mt-12 mb-4 font-sans" data-id="element-51">
              Barely Any Spots at 45 – Covered in Them by 48
            </h3>
            <p data-id="element-52">Maybe this sounds familiar:</p>
            <p data-id="element-53">
              You stand in front of the mirror every morning and the first thing
              you see is those brown spots. Not the wrinkles. Not the tiredness.
              The spots.
            </p>
            <p data-id="element-54">
              They've destroyed the even complexion you used to have. You take a
              photo with your kids or grandkids – and immediately delete it. "I
              look so old," you think.
            </p>

            {/* IMAGE PLACEHOLDER */}
            <figure className="my-8" data-id="element-55">
              <img src="https://cdn.magicpatterns.com/uploads/ge6dnRKwwcBPYwVFFr5EUk/Bildschirmfoto_2026-01-20_um_19.01.56.png" alt="Before/After comparison - Visible reduction of dark spots after 12 weeks" className="w-full rounded-lg shadow-lg" data-id="element-56" />
              <figcaption className="text-sm text-gray-500 mt-2 text-center italic" data-id="element-57">
                Typical pigmentation changes during menopause – Before and after
                12 weeks of use
              </figcaption>
            </figure>

            <p data-id="element-58">
              You need more and more foundation to cover the spots. But even
              that only helps so much. And the most frustrating part? You've
              already tried everything.
            </p>
            <p data-id="element-59">
              Vitamin C serums. Expensive brightening creams from Lancôme,
              Clarins, Shiseido. Chemical peels. Maybe even laser treatments at
              the dermatologist's office.
            </p>
            <p data-id="element-60">
              And every time, the same pattern: The spots fade... for 2-3 weeks.
              Then they come back. Sometimes even darker than before.
            </p>
          </div>

          {/* SCIENCE SECTION - BLUE BOX */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-[#1e3a5f] text-white p-8 md:p-12 rounded-2xl my-12 shadow-xl" data-id="element-61">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 font-sans border-b border-blue-400 pb-4" data-id="element-62">
              The Truth the Skincare Industry Won't Tell You
            </h3>
            <div className="space-y-6 text-blue-50 text-lg leading-relaxed" data-id="element-63">
              <p data-id="element-64">
                Here's the thing: These spots are not NEW. They're UV damage
                from your 20s and 30s – every summer day at the beach, every ski
                trip without sunscreen.
              </p>
              <p data-id="element-65">
                That damage was stored in your skin like invisible ink. For
                decades, it was there. Beneath the surface. Invisible.
              </p>
              <p className="font-bold text-white text-xl" data-id="element-66">
                But then menopause hit.
              </p>
              <p data-id="element-67">
                And suddenly – within just 2-3 years – those "sleeping" damage
                sites became visible. Why? Because estrogen had been regulating
                that damage for decades.
              </p>

              <div className="bg-blue-900/50 p-6 rounded-lg border border-blue-700 my-6" data-id="element-68">
                <p className="italic font-serif text-lg mb-2" data-id="element-69">
                  "Sex steroids regulate skin pigmentation through nonclassical
                  membrane-bound receptors."
                </p>
                <p className="text-sm text-blue-300 uppercase tracking-wider font-bold" data-id="element-70">
                  – PubMed Central Study, 2016 (confirmed 2024)
                </p>
              </div>

              <p data-id="element-71">
                In simple terms: Estrogen acted as a molecular "lid" on those
                old UV damage sites. But with the estrogen drop during
                menopause, that lid comes off.
              </p>
              <p data-id="element-72">
                The "hidden" melanin suddenly oxidizes – like a cut apple
                turning brown. That's why it feels so sudden. This is the{' '}
                <span className="text-yellow-400 font-bold" data-id="element-73">
                  Pigment Time Bomb
                </span>
                .
              </p>
            </div>
          </motion.div>

          {/* WHY PRODUCTS FAIL */}
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed font-serif mb-12" data-id="element-74">
            <h3 className="text-2xl font-bold mb-4 font-sans" data-id="element-75">
              Why Everything You've Tried So Far Hasn't Worked
            </h3>
            <p data-id="element-76">
              Now you understand why your brightening creams only helped
              temporarily. They only treat the SYMPTOM – not the CAUSE.
            </p>
            <ul className="list-none pl-0 space-y-4 my-6" data-id="element-77">
              <li className="flex items-start" data-id="element-78">
                <AlertCircle className="w-6 h-6 text-red-500 mr-3 mt-1 shrink-0" data-id="element-79" />
                <span data-id="element-80">
                  <strong data-id="element-81">Vitamin C & Arbutin:</strong> Only brighten the
                  surface, but don't stop the oxidation process happening deep
                  within.
                </span>
              </li>
              <li className="flex items-start" data-id="element-82">
                <AlertCircle className="w-6 h-6 text-red-500 mr-3 mt-1 shrink-0" data-id="element-83" />
                <span data-id="element-84">
                  <strong data-id="element-85">Laser treatments:</strong> Remove visible spots for
                  $200-500, but the "time bomb" keeps ticking. 6-12 months
                  later, new spots appear.
                </span>
              </li>
              <li className="flex items-start" data-id="element-86">
                <AlertCircle className="w-6 h-6 text-red-500 mr-3 mt-1 shrink-0" data-id="element-87" />
                <span data-id="element-88">
                  <strong data-id="element-89">Luxury brands:</strong> Often generic "anti-aging"
                  formulas, not specifically developed for the hormonal estrogen
                  decline.
                </span>
              </li>
            </ul>
          </div>

          {/* DR MÜLLER STORY */}
          <div className="bg-gray-50 p-8 md:p-10 rounded-2xl border border-gray-100 mb-12" data-id="element-90">
            <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-91">
              <div className="w-full md:w-1/3 shrink-0" data-id="element-92">
                <img src="https://cdn.magicpatterns.com/uploads/9mtbAzUQLkN3R9yDQGqH6P/pasted-image.png" alt="Dr. Evelyn Müller - Dermatologist & Researcher" className="rounded-lg w-full object-cover aspect-[3/4]" data-id="element-93" />
                <p className="text-sm text-center mt-2 text-gray-500" data-id="element-94">
                  Dermatologist & Researcher
                </p>
              </div>
              <div className="prose prose-lg text-gray-800 font-serif" data-id="element-95">
                <h3 className="text-2xl font-bold font-sans mt-0" data-id="element-96">
                  "I had the same problem – and I knew my own products weren't
                  really working"
                </h3>
                <p data-id="element-97">
                  Dr. Evelyn Miller is a board-certified dermatologist with over
                  20 years of experience. But her story doesn't begin in the lab
                  – it begins in front of the mirror.
                </p>
                <p data-id="element-98">
                  "At 40, I barely had any wrinkles. Then everything hit at
                  once. Suddenly there were spots I'd never seen before. And the
                  worst part: As a dermatologist, I knew that my own products
                  weren't really working."
                </p>
                <p data-id="element-99">
                  In her research, she came across studies on estrogen and
                  melanin distribution. "That was my aha moment. The spots
                  aren't NEW. They're ghosts from my past. Estrogen had just
                  been keeping them invisible."
                </p>
                <p data-id="element-100">
                  The result of years of research?{' '}
                  <strong data-id="element-101">The Meno-Barrier Formula™.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* SOLUTION PILLARS */}
          <div className="mb-16" data-id="element-102">
            <h3 className="text-3xl font-bold text-center mb-4 font-sans" data-id="element-103">
              The Oxidation-Stopper Method
            </h3>
            <p className="text-center text-gray-600 max-w-2xl mx-auto mb-10 text-lg" data-id="element-104">
              To defuse the Pigment Time Bomb, you need three things working
              simultaneously. Most products only address ONE of them. That's why
              they fail.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-105">
              <SciencePillar number={1} title="Stop Oxidation" description="Instead of 'erasing' pigment, the formula stops the oxidation process. Niacinamide 3% prevents oxidized melanin from migrating – like a bouncer at the door." ingredients={['Niacinamide 3%', 'Alpha-Arbutin 1.5%']} data-id="element-106" />
              <SciencePillar number={2} title="Repair the Barrier" description="Without an intact barrier, active ingredients can't penetrate. Squalane fills the gaps that hormonal decline has left behind." ingredients={['Squalane 0.5% (Bio-identical)', 'Oat Kernel Extract']} data-id="element-107" />
              <SciencePillar number={3} title="Protect Collagen" description="Trehalose stabilizes existing collagen structures like a protective shield and prevents denaturation from oxidative stress." ingredients={['Trehalose 1.96%', 'Collagen Stimulation']} data-id="element-108" />
            </div>
          </div>

          {/* TESTIMONIALS */}
          <div className="bg-white py-8 mb-12" data-id="element-109">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-10 font-sans" data-id="element-110">
              "After 6 Weeks, My Spots Have Visibly Faded – For the First Time
              in Years"
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-111">
              <TestimonialCard quote="I've had these brown spots on my forehead since menopause. Nothing worked. After 6 weeks with the Meno-Barrier Formula, they've visibly faded. For the first time in years, I go out during the day without makeup." author="Martha K." age={53} location="Houston, TX" data-id="element-112" />
              <TestimonialCard quote="My skin was so dry it constantly felt tight. Since I started using the Meno-Barrier Formula, moisture finally stays IN my skin. The feeling is indescribable." author="Claudia R." age={49} location="Scottsdale, AZ" data-id="element-113" />
              <TestimonialCard quote="At 50, I didn't recognize myself anymore. Tired, dull, spots everywhere. After 3 months, my daughter said: 'Mom, you look so refreshed.' I almost cried." author="Sabine W." age={52} location="Portland, OR" data-id="element-114" />
              <TestimonialCard quote="As a chemist, I looked at the ingredient list and was impressed – real concentrations, not diluted. 3% Niacinamide, 1.5% Alpha-Arbutin. And it actually works." author="Dr. Andrea H." age={56} location="Boston, MA" data-id="element-115" />
            </div>
            <div className="mt-8 text-center bg-green-50 p-4 rounded-lg border border-green-100 inline-block w-full" data-id="element-116">
              <p className="font-bold text-green-800" data-id="element-117">
                <CheckCircle className="w-5 h-5 inline mr-2 mb-1" data-id="element-118" />
                In an independent user study, 87% of participants reported
                visible improvement in dark spots after 4-8 weeks.
              </p>
            </div>
          </div>

          {/* TIMELINE */}
          <div className="mb-16 max-w-3xl mx-auto" data-id="element-119">
            <h3 className="text-2xl font-bold mb-8 font-sans text-center" data-id="element-120">
              What You Can Realistically Expect
            </h3>
            <div className="space-y-6 relative border-l-2 border-blue-100 pl-8 ml-4" data-id="element-121">
              <div className="relative" data-id="element-122">
                <span className="absolute -left-[41px] top-0 w-6 h-6 bg-blue-500 rounded-full border-4 border-white shadow-sm" data-id="element-123"></span>
                <h4 className="font-bold text-lg text-[#1e3a5f]" data-id="element-124">
                  IMMEDIATELY (Days 1-3)
                </h4>
                <p className="text-gray-600" data-id="element-125">
                  Your skin feels smoother. Less tight. The barrier begins to
                  seal itself.
                </p>
              </div>
              <div className="relative" data-id="element-126">
                <span className="absolute -left-[41px] top-0 w-6 h-6 bg-blue-500 rounded-full border-4 border-white shadow-sm" data-id="element-127"></span>
                <h4 className="font-bold text-lg text-[#1e3a5f]" data-id="element-128">
                  AFTER 2 WEEKS
                </h4>
                <p className="text-gray-600" data-id="element-129">
                  Dryness is noticeably reduced. Your skin "holds" moisture
                  better. You need to reapply less often.
                </p>
              </div>
              <div className="relative" data-id="element-130">
                <span className="absolute -left-[41px] top-0 w-6 h-6 bg-blue-500 rounded-full border-4 border-white shadow-sm" data-id="element-131"></span>
                <h4 className="font-bold text-lg text-[#1e3a5f]" data-id="element-132">
                  AFTER 4-8 WEEKS
                </h4>
                <p className="text-gray-600" data-id="element-133">
                  Now you see the visible improvement in dark spots. Your
                  complexion becomes more even. The spots fade.
                </p>
              </div>
              <div className="relative" data-id="element-134">
                <span className="absolute -left-[41px] top-0 w-6 h-6 bg-blue-500 rounded-full border-4 border-white shadow-sm" data-id="element-135"></span>
                <h4 className="font-bold text-lg text-[#1e3a5f]" data-id="element-136">
                  AFTER 12 WEEKS
                </h4>
                <p className="text-gray-600" data-id="element-137">
                  The complete transformation. Improved skin density. More
                  firmness. An even, radiant complexion.
                </p>
              </div>
            </div>
          </div>

          {/* OBJECTIONS & FAQ */}
          <div className="bg-gray-50 p-8 rounded-xl mb-12" data-id="element-138">
            <h3 className="text-2xl font-bold mb-6 font-sans" data-id="element-139">
              Frequently Asked Questions
            </h3>
            <div className="space-y-6" data-id="element-140">
              <div data-id="element-141">
                <h4 className="font-bold text-lg mb-2" data-id="element-142">
                  "But I've already tried everything..."
                </h4>
                <p className="text-gray-700" data-id="element-143">
                  Here's the difference: Every other product only treated ONE
                  symptom. The Meno-Barrier Formula is the first formula that
                  addresses all three processes simultaneously: Oxidation Stop +
                  Barrier Repair + Collagen Protection.
                </p>
              </div>
              <div data-id="element-144">
                <h4 className="font-bold text-lg mb-2" data-id="element-145">
                  "Is my skin too sensitive?"
                </h4>
                <p className="text-gray-700" data-id="element-146">
                  Menopausal skin is more sensitive. That's why the formula is
                  fragrance-free, without essential oils, and without harsh
                  alcohols. Instead of "bleaching agents," we use gentle
                  regulation.
                </p>
              </div>
              <div data-id="element-147">
                <h4 className="font-bold text-lg mb-2" data-id="element-148">
                  "Is it too late for me? I'm already 60..."
                </h4>
                <p className="text-gray-700" data-id="element-149">
                  No. Your skin renews itself every 28 days – even at 60. It's
                  never too late to give your skin the right building blocks.
                </p>
              </div>
            </div>
          </div>

          {/* SCARCITY & PRICING */}
          <div className="bg-white p-6 md:p-10 rounded-2xl shadow-xl border border-gray-200 relative overflow-hidden mb-12" data-id="element-150">
            <div className="relative z-10" data-id="element-151">
              {/* Heading with lines */}
              <div className="flex items-center gap-4 mb-8" data-id="element-152">
                <div className="flex-1 h-px bg-[#1e3a5f]" data-id="element-153"></div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1e3a5f] font-serif whitespace-nowrap" data-id="element-154">
                  Sichere dir deinen Vorrat
                </h3>
                <div className="flex-1 h-px bg-[#1e3a5f]" data-id="element-155"></div>
              </div>

              {/* Supply Options */}
              <div className="space-y-4 mb-6" data-id="element-156">
                {/* 1 Month */}
                <label className="flex items-center justify-between border-2 border-blue-100 rounded-full px-5 py-4 cursor-pointer hover:border-blue-300 transition-colors" data-id="element-157">
                  <div className="flex items-center gap-3" data-id="element-158">
                    <input type="radio" name="supply" className="w-5 h-5 accent-[#1e3a5f]" data-id="element-159" />
                    <div data-id="element-160">
                      <div className="flex items-center gap-3 flex-wrap" data-id="element-161">
                        <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-162">
                          1 Monat Vorrat
                        </span>
                        <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-163">
                          Spare €20,00 /Stk.
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4" data-id="element-164">
                    <div className="font-bold text-[#1e3a5f] text-2xl" data-id="element-165">
                      €39,95{' '}
                      <span className="text-base font-normal" data-id="element-166">/Stück</span>
                    </div>
                    <div className="text-gray-400 line-through text-sm" data-id="element-167">
                      €59,95
                    </div>
                  </div>
                </label>

                {/* 6 Months - Best Value */}
                <label className="flex items-center justify-between border-2 border-blue-200 bg-blue-50/30 rounded-full px-5 py-4 cursor-pointer hover:border-blue-300 transition-colors" data-id="element-168">
                  <div className="flex items-center gap-3" data-id="element-169">
                    <input type="radio" name="supply" defaultChecked className="w-5 h-5 accent-[#1e3a5f]" data-id="element-170" />
                    <div data-id="element-171">
                      <div className="flex items-center gap-3 flex-wrap" data-id="element-172">
                        <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-173">
                          6 Monate Vorrat
                        </span>
                        <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-174">
                          Spare €30,00 /Stk.
                        </span>
                      </div>
                      <div className="text-gray-500 text-sm mt-0.5" data-id="element-175">
                        Du sparst 50.04%
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4" data-id="element-176">
                    <div className="font-bold text-[#1e3a5f] text-2xl" data-id="element-177">
                      €29,95{' '}
                      <span className="text-base font-normal" data-id="element-178">/Stück</span>
                    </div>
                    <div className="text-gray-400 line-through text-sm" data-id="element-179">
                      €59,95
                    </div>
                  </div>
                </label>

                {/* 3 Months */}
                <label className="flex items-center justify-between border-2 border-blue-100 rounded-full px-5 py-4 cursor-pointer hover:border-blue-300 transition-colors" data-id="element-180">
                  <div className="flex items-center gap-3" data-id="element-181">
                    <input type="radio" name="supply" className="w-5 h-5 accent-[#1e3a5f]" data-id="element-182" />
                    <div data-id="element-183">
                      <div className="flex items-center gap-3 flex-wrap" data-id="element-184">
                        <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-185">
                          3 Monate Vorrat
                        </span>
                        <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-186">
                          Spare €25,00 /Stk.
                        </span>
                      </div>
                      <div className="text-gray-500 text-sm mt-0.5" data-id="element-187">
                        Du sparst 41.70%
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4" data-id="element-188">
                    <div className="font-bold text-[#1e3a5f] text-2xl" data-id="element-189">
                      €34,95{' '}
                      <span className="text-base font-normal" data-id="element-190">/Stück</span>
                    </div>
                    <div className="text-gray-400 line-through text-sm" data-id="element-191">
                      €59,95
                    </div>
                  </div>
                </label>
              </div>

              {/* Subscribe Option */}
              <label className="flex items-center gap-3 border-2 border-dashed border-[#1e3a5f] rounded-xl px-5 py-4 cursor-pointer hover:bg-blue-50/30 transition-colors mb-8" data-id="element-192">
                <input type="checkbox" className="w-5 h-5 accent-[#1e3a5f] rounded" data-id="element-193" />
                <div data-id="element-194">
                  <div className="font-bold text-[#1e3a5f] text-lg" data-id="element-195">
                    Abonnieren und 10% sparen
                  </div>
                  <div className="text-gray-500 text-sm" data-id="element-196">
                    Mit kostenlosem Versand
                  </div>
                </div>
              </label>

              {/* CTA Button */}
              <a href="https://www.skincurrent.de/products/anti-aging-creme" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-lg text-xl shadow-lg transition-colors inline-flex items-center group no-underline" data-id="element-197">
                JETZT SPAREN & BESTELLEN
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-198" />
              </a>

              <p className="text-xs text-gray-400 mt-4 text-center" data-id="element-199">
                *60-day money-back guarantee. Zero risk.
              </p>
            </div>
          </div>

          {/* FINAL CLOSING */}
          <div className="mt-16 text-center max-w-2xl mx-auto" data-id="element-200">
            <h3 className="text-2xl font-bold mb-4 font-sans" data-id="element-201">
              In 12 Weeks, You'll Look in the Mirror – and Recognize Yourself
              Again
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-202">
              The Pigment Time Bomb keeps ticking every day. Every day you wait,
              those "sleeping" UV damage sites continue to oxidize. Do you want
              to look back in 12 weeks and think: "I wish I had started back
              then"?
            </p>
            <button className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-lg text-xl shadow-lg transition-colors inline-flex items-center group" data-id="element-203">
              SAVE NOW & ORDER
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-204" />
            </button>
          </div>
        </article>
      </main>

      <footer className="bg-gray-100 py-12 mt-12 border-t border-gray-200" data-id="element-205">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm" data-id="element-206">
          <p className="mb-4" data-id="element-207">© 2024 SkinCurrent. All rights reserved.</p>
          <p className="max-w-2xl mx-auto text-xs leading-relaxed" data-id="element-208">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease. Individual results may vary.
          </p>
          <div className="mt-6 space-x-4" data-id="element-209">
            <a href="#" className="hover:text-gray-800 underline" data-id="element-210">
              Legal Notice
            </a>
            <a href="#" className="hover:text-gray-800 underline" data-id="element-211">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-gray-800 underline" data-id="element-212">
              Terms & Conditions
            </a>
          </div>
        </div>
      </footer>
    </div>;
}